var _StyledDivider, _HelpOutline, _SettingsOutlined, _AccountCircleOutline, _MoreVert, _LastPageOutlined, _FirstPageOutlined;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useState } from 'react';
import { arrayOf, bool, element, elementType, exact, func, object, oneOf, oneOfType, shape, string } from 'prop-types';
import { buttonClasses } from '@mui/material/Button';
import { AccountCircleOutlined, FirstPageOutlined, HelpOutline, LastPageOutlined, MoreVert, SettingsOutlined } from 'mosaic-react-icons';
import { alpha, styled, useTheme } from '@mui/material/styles';
import SovosIconButton from '../../../sovos-icon-button';
import SovosDivider from '../../../sovos-divider';
import { collapsedWidthFactor } from '../../../themes/sovos-sizes';
import itemTypes from '../../../internals/utils/itemTypes';
import { getIconSizes, spacingToNumber } from '../../../internals/utils';
import useMosaicTranslation from '../../../internals/i18n/useMosaicTranslation';
import { BaseMenu, renderMenuItems, renderUserMenuHeader } from '../../../internals/navigation/menus';
const Root = styled('footer', {
  shouldForwardProp: prop => prop !== 'isCollapsed' && prop !== 'navFooterHeight'
})(_ref => {
  let {
    isCollapsed,
    navFooterHeight,
    theme: {
      palette,
      spacing
    }
  } = _ref;
  return {
    alignItems: 'center',
    backgroundColor: alpha(palette.common.black, 0.16),
    display: 'flex',
    flexDirection: isCollapsed ? 'column' : 'row',
    flexWrap: isCollapsed ? 'wrap' : 'nowrap',
    height: isCollapsed ? 'auto' : navFooterHeight,
    justifyContent: isCollapsed ? 'center' : 'flex-end',
    padding: isCollapsed ? spacing() : "0 ".concat(spacing())
  };
});
const StyledIconButton = styled(SovosIconButton, {
  shouldForwardProp: prop => prop !== 'isCollapsed'
})(_ref2 => {
  var _components$SovosNavi, _components$SovosNavi2, _components$SovosNavi3, _components$SovosNavi4, _components$SovosNavi5, _components$SovosNavi6, _components$SovosNavi7, _components$SovosNavi8, _components$SovosNavi9, _components$SovosNavi10, _components$SovosNavi11, _components$SovosNavi12;
  let {
    isCollapsed,
    theme: {
      components,
      palette,
      shape: themeShape,
      spacing
    }
  } = _ref2;
  return {
    backgroundColor: alpha((_components$SovosNavi = components === null || components === void 0 || (_components$SovosNavi2 = components.SovosNavigation) === null || _components$SovosNavi2 === void 0 || (_components$SovosNavi2 = _components$SovosNavi2.styleOverrides) === null || _components$SovosNavi2 === void 0 ? void 0 : _components$SovosNavi2.textColor) !== null && _components$SovosNavi !== void 0 ? _components$SovosNavi : palette.primary.contrastText, 0.08),
    borderRadius: themeShape.borderRadius,
    color: alpha((_components$SovosNavi3 = components === null || components === void 0 || (_components$SovosNavi4 = components.SovosNavigation) === null || _components$SovosNavi4 === void 0 || (_components$SovosNavi4 = _components$SovosNavi4.styleOverrides) === null || _components$SovosNavi4 === void 0 ? void 0 : _components$SovosNavi4.textColor) !== null && _components$SovosNavi3 !== void 0 ? _components$SovosNavi3 : palette.primary.contrastText, 0.72),
    margin: "0 ".concat(spacing(isCollapsed ? 0 : 0.5), " ").concat(spacing(isCollapsed ? 1 : 0), " ").concat(spacing(isCollapsed ? 0 : 0.5)),
    '&:hover': {
      backgroundColor: alpha((_components$SovosNavi5 = components === null || components === void 0 || (_components$SovosNavi6 = components.SovosNavigation) === null || _components$SovosNavi6 === void 0 || (_components$SovosNavi6 = _components$SovosNavi6.styleOverrides) === null || _components$SovosNavi6 === void 0 ? void 0 : _components$SovosNavi6.textColor) !== null && _components$SovosNavi5 !== void 0 ? _components$SovosNavi5 : palette.primary.contrastText, 0.12),
      color: (_components$SovosNavi7 = components === null || components === void 0 || (_components$SovosNavi8 = components.SovosNavigation) === null || _components$SovosNavi8 === void 0 || (_components$SovosNavi8 = _components$SovosNavi8.styleOverrides) === null || _components$SovosNavi8 === void 0 ? void 0 : _components$SovosNavi8.textColor) !== null && _components$SovosNavi7 !== void 0 ? _components$SovosNavi7 : palette.primary.contrastText
    },
    ["&.".concat(buttonClasses.focusVisible)]: {
      backgroundColor: alpha((_components$SovosNavi9 = components === null || components === void 0 || (_components$SovosNavi10 = components.SovosNavigation) === null || _components$SovosNavi10 === void 0 || (_components$SovosNavi10 = _components$SovosNavi10.styleOverrides) === null || _components$SovosNavi10 === void 0 ? void 0 : _components$SovosNavi10.textColor) !== null && _components$SovosNavi9 !== void 0 ? _components$SovosNavi9 : palette.primary.contrastText, 0.12),
      color: (_components$SovosNavi11 = components === null || components === void 0 || (_components$SovosNavi12 = components.SovosNavigation) === null || _components$SovosNavi12 === void 0 || (_components$SovosNavi12 = _components$SovosNavi12.styleOverrides) === null || _components$SovosNavi12 === void 0 ? void 0 : _components$SovosNavi12.textColor) !== null && _components$SovosNavi11 !== void 0 ? _components$SovosNavi11 : palette.primary.contrastText
    },
    '&.sovosNavigationFooter__toggle': {
      margin: "0 ".concat(isCollapsed ? 0 : 'auto', " 0 0")
    },
    '&:last-of-type:not(.sovosNavigationFooter__toggle)': {
      marginRight: "0"
    }
  };
});
const StyledDivider = styled(SovosDivider)(_ref3 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref3;
  return {
    backgroundColor: palette.divider,
    margin: "".concat(spacing(0.5), " ").concat(spacing(2))
  };
});
const MenuItemPropType = shape({
  action: func,
  icon: elementType,
  label: string,
  type: oneOf([itemTypes.DIVIDER, itemTypes.CONTENT])
});
const AdditionalButtonPropType = shape({
  disabled: bool,
  icon: element.isRequired,
  label: string.isRequired,
  action: func.isRequired
});
const NavigationFooter = _ref4 => {
  let {
    additionalButtons: additionalButtonsProp = [],
    handleModalClose,
    isCollapsed = false,
    labels,
    onCollapseClicked,
    onHelpClicked,
    settingsMenuItems = [],
    showSettingsButton = false,
    showUserButton = true,
    showHelpButton = false,
    userMenuItems = [],
    userName,
    slotProps = {},
    slots = {}
  } = _ref4;
  const {
    t
  } = useMosaicTranslation();
  const {
    spacing
  } = useTheme();
  const buttonSizeKey = 'small';
  const buttonSize = () => {
    const sizes = getIconSizes(spacing);
    return sizes[buttonSizeKey].button.height;
  };
  const navFooterHeight = buttonSize() + spacingToNumber(spacing(2));
  const [menuOpen, setMenuOpen] = useState(false);
  const [menu, setMenu] = useState({});
  const moreMenuAnchor = useRef(null);
  const userMenuAnchor = useRef(null);
  const settingsMenuAnchor = useRef(null);
  const deprecatedShowSettings = !slots.settingsMenu && !slotProps.settingsMenu && showSettingsButton && settingsMenuItems && settingsMenuItems.length;
  const showSettings = slots.settingsMenu || slotProps.settingsMenu;
  const deprecatedShowUser = !slots.userMenu && !slotProps.userMenu && showUserButton && userMenuItems && userMenuItems.length;
  const showUser = slots.userMenu || slotProps.userMenu;
  const hideMenu = () => {
    setMenuOpen(false);
  };
  const handleHelpClicked = () => {
    onHelpClicked();
    handleModalClose();
  };
  const handleMenuClick = action => {
    hideMenu();
    handleModalClose();
    action();
  };
  const showMenu = (name, anchor) => {
    setMenu({
      name,
      anchor
    });
    setMenuOpen(true);
  };
  const renderAdditionalButtons = () => {
    var _slotProps$additionFo;
    const buttonProps = (_slotProps$additionFo = slotProps.additionFooterIconButtons) !== null && _slotProps$additionFo !== void 0 ? _slotProps$additionFo : additionalButtonsProp.map(bp => ({
      children: bp.icon,
      disabled: bp.disabled,
      tooltipText: bp.label,
      onClick: bp.action
    }));
    return buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.map((bp, idx) => /*#__PURE__*/React.createElement(StyledIconButton, _extends({
      key: bp.tooltipText || idx,
      className: "sovosNavigationFooter__additionalButton",
      isCollapsed: isCollapsed,
      size: buttonSizeKey
    }, bp)));
  };
  const renderMenu = () => {
    var _menu$anchor, _slotProps$userMenu2;
    const menuTop = (navFooterHeight - buttonSize()) / 2 + spacingToNumber(spacing());
    const collapsedMenuX = (spacingToNumber(spacing(collapsedWidthFactor)) - buttonSize()) / 2 + buttonSize() + spacingToNumber(spacing());
    const baseMenuProps = {
      anchorEl: (_menu$anchor = menu.anchor) === null || _menu$anchor === void 0 ? void 0 : _menu$anchor.current,
      anchorOrigin: {
        horizontal: isCollapsed ? collapsedMenuX : 0,
        vertical: isCollapsed ? 'bottom' : -menuTop
      },
      marginThreshold: 10,
      onClose: hideMenu,
      open: menuOpen,
      transformOrigin: {
        horizontal: 'left',
        vertical: 'bottom'
      }
    };
    if (menu.name === 'settings' && slots.settingsMenu) {
      return /*#__PURE__*/React.createElement(slots.settingsMenu, _extends({}, slotProps.settingsMenu, baseMenuProps));
    }
    if (menu.name === 'user' && slots.userMenu) {
      return /*#__PURE__*/React.createElement(slots.userMenu, _extends({}, slotProps.userMenu, baseMenuProps));
    }
    let menuItems = [];
    const baseMenuItemProps = {
      classNameRoot: 'sovosNavigationFooter',
      handleMenuClick,
      menuName: menu.name
    };
    if (menu.name === 'more') {
      if (deprecatedShowUser) {
        menuItems.push(renderMenuItems({
          ...baseMenuItemProps,
          items: userMenuItems
        }));
      }
      if (deprecatedShowSettings) {
        if (deprecatedShowUser) {
          menuItems.push(_StyledDivider || (_StyledDivider = /*#__PURE__*/React.createElement(StyledDivider, {
            key: "more-divider"
          })));
        }
        menuItems.push(...renderMenuItems({
          ...baseMenuItemProps,
          items: settingsMenuItems
        }));
      }
    } else if (menu.name === 'user') {
      var _slotProps$userMenu$i, _slotProps$userMenu;
      menuItems = renderMenuItems({
        ...baseMenuItemProps,
        items: (_slotProps$userMenu$i = (_slotProps$userMenu = slotProps.userMenu) === null || _slotProps$userMenu === void 0 ? void 0 : _slotProps$userMenu.items) !== null && _slotProps$userMenu$i !== void 0 ? _slotProps$userMenu$i : userMenuItems
      });
    } else {
      var _slotProps$settingsMe, _slotProps$settingsMe2;
      menuItems = renderMenuItems({
        ...baseMenuItemProps,
        items: (_slotProps$settingsMe = (_slotProps$settingsMe2 = slotProps.settingsMenu) === null || _slotProps$settingsMe2 === void 0 ? void 0 : _slotProps$settingsMe2.items) !== null && _slotProps$settingsMe !== void 0 ? _slotProps$settingsMe : settingsMenuItems
      });
    }
    return /*#__PURE__*/React.createElement(BaseMenu, _extends({
      className: "sovosNavigationFooter__".concat(menu.name, "Menu")
    }, baseMenuProps), menuItems, renderUserMenuHeader({
      menuName: menu.name,
      userMenuProps: (_slotProps$userMenu2 = slotProps.userMenu) !== null && _slotProps$userMenu2 !== void 0 ? _slotProps$userMenu2 : {},
      userName
    }));
  };
  const renderHelpButton = () => {
    var _slotProps$helpButton;
    const buttonProps = (_slotProps$helpButton = slotProps.helpButton) !== null && _slotProps$helpButton !== void 0 ? _slotProps$helpButton : {
      onClick: handleHelpClicked
    };
    return /*#__PURE__*/React.createElement(StyledIconButton, _extends({
      className: "sovosNavigationFooter__helpButton",
      isCollapsed: isCollapsed,
      size: buttonSizeKey,
      tooltipText: (labels === null || labels === void 0 ? void 0 : labels.helpIcon) || t('navigationFooter.help')
    }, buttonProps), _HelpOutline || (_HelpOutline = /*#__PURE__*/React.createElement(HelpOutline, null)));
  };
  const renderSettingsButton = () => /*#__PURE__*/React.createElement(StyledIconButton, {
    className: "sovosNavigationFooter__settingsButton",
    isCollapsed: isCollapsed,
    onClick: () => showMenu('settings', settingsMenuAnchor),
    ref: settingsMenuAnchor,
    size: buttonSizeKey,
    tooltipText: (labels === null || labels === void 0 ? void 0 : labels.settingsIcon) || t('settings')
  }, _SettingsOutlined || (_SettingsOutlined = /*#__PURE__*/React.createElement(SettingsOutlined, null)));
  const renderUserButton = () => /*#__PURE__*/React.createElement(StyledIconButton, {
    className: "sovosNavigationFooter__userButton",
    isCollapsed: isCollapsed,
    onClick: () => showMenu('user', userMenuAnchor),
    ref: userMenuAnchor,
    size: buttonSizeKey,
    tooltipText: (labels === null || labels === void 0 ? void 0 : labels.userIcon) || t('yourAccount')
  }, _AccountCircleOutline || (_AccountCircleOutline = /*#__PURE__*/React.createElement(AccountCircleOutlined, null)));
  const renderCollapsedButtons = () => {
    if (isCollapsed) return /*#__PURE__*/React.createElement(React.Fragment, null, renderAdditionalButtons(), showSettings && renderSettingsButton(), showUser && renderUserButton(), (deprecatedShowUser || deprecatedShowSettings) && /*#__PURE__*/React.createElement(StyledIconButton, {
      sx: {
        mb: 1
      },
      isCollapsed: isCollapsed,
      onClick: () => showMenu('more', moreMenuAnchor),
      ref: moreMenuAnchor,
      size: buttonSizeKey,
      tooltipText: (labels === null || labels === void 0 ? void 0 : labels.moreIcon) || t('more')
    }, _MoreVert || (_MoreVert = /*#__PURE__*/React.createElement(MoreVert, null))));
    return undefined;
  };
  const renderExpandedButtons = () => {
    if (!isCollapsed) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, renderAdditionalButtons(), slotProps.helpButton || showHelpButton ? renderHelpButton() : undefined, showSettings || deprecatedShowSettings ? renderSettingsButton() : undefined, showUser || deprecatedShowUser ? renderUserButton() : undefined);
    }
    return undefined;
  };
  return /*#__PURE__*/React.createElement(Root, {
    className: "sovosNavigationFooter",
    isCollapsed: isCollapsed,
    navFooterHeight: navFooterHeight
  }, renderCollapsedButtons(), /*#__PURE__*/React.createElement(StyledIconButton, {
    className: "sovosNavigationFooter__toggle",
    isCollapsed: isCollapsed,
    onClick: onCollapseClicked,
    size: buttonSizeKey,
    tooltipText: isCollapsed ? (labels === null || labels === void 0 ? void 0 : labels.expandIcon) || t('navigationFooter.expand') : (labels === null || labels === void 0 ? void 0 : labels.collapseIcon) || t('navigationFooter.collapse')
  }, isCollapsed ? _LastPageOutlined || (_LastPageOutlined = /*#__PURE__*/React.createElement(LastPageOutlined, null)) : _FirstPageOutlined || (_FirstPageOutlined = /*#__PURE__*/React.createElement(FirstPageOutlined, null))), renderExpandedButtons(), renderMenu());
};
NavigationFooter.propTypes = {
  /**
   * @deprecated
   */
  additionalButtons: arrayOf(AdditionalButtonPropType),
  isCollapsed: bool,
  handleModalClose: func.isRequired,
  labels: exact({
    collapseIcon: string,
    expandIcon: string,
    helpIcon: string,
    moreIcon: string,
    settingsIcon: string,
    userIcon: string
  }),
  onCollapseClicked: func,
  /**
   * @deprecated
   */
  onHelpClicked: func,
  /**
   * @deprecated
   */
  showSettingsButton: bool,
  /**
   * @deprecated
   */
  showUserButton: bool,
  /**
   * @deprecated
   */
  showHelpButton: bool,
  /**
   * @deprecated
   */
  settingsMenuItems: arrayOf(MenuItemPropType),
  slotProps: shape({
    additionFooterIconButtons: arrayOf(shape({
      children: element.isRequired,
      disabled: bool,
      onClick: func.isRequired,
      tooltipText: string.isRequired
    })),
    helpButton: exact({
      onClick: func
    }),
    settingsMenu: oneOfType([object, exact({
      items: arrayOf(MenuItemPropType)
    })]),
    userMenu: oneOfType([object, exact({
      email: string,
      items: arrayOf(MenuItemPropType),
      name: string,
      surname: string
    })])
  }),
  slots: shape({
    settingsMenu: elementType,
    userMenu: elementType
  }),
  /**
   * @deprecated
   */
  userName: string,
  /**
   * @deprecated
   */
  userMenuItems: arrayOf(MenuItemPropType)
};
export default NavigationFooter;